import Stack from '@mui/material/Stack'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Outlet } from 'react-router-dom'
import { RxCollection } from 'rxdb'
import { useRxData } from 'rxdb-hooks'

import { LoginButton } from '@/modules/auth/components/LoginButton.tsx'
import { UserContext } from '@/modules/auth/context.ts'
import { useAuthState } from '@/modules/auth/hooks/useAuthState.ts'
import { AppLoader } from '@/modules/components/AppLoader.tsx'
import { OfflineErrorAlerts } from '@/modules/components/OfflineErrorAlerts'
import { Footer } from '@/modules/layouts/Footer.tsx'
import { Header } from '@/modules/layouts/Header.tsx'
import { LoginPage } from '@/modules/layouts/LoginPage.tsx'
import { PostHogAnalytics } from '@/system/components/PostHogAnalytics.tsx'
import { AppUser } from '@/system/rxdb/schemas/appUser.ts'

import messages from './messages.ts'

const contentSx = {
  minHeight: '100vh',
}

const mainSx = {
  flex: '1 0 100%',
  height: '100%',
}

export const Layout = () => {
  const { observeAuthenticationState } = useAuthState()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const queryConstructor = useCallback(
    (collection: RxCollection) => collection.findOne(),
    []
  )
  const { result, isFetching } = useRxData<AppUser>('appuser', queryConstructor)

  const user = useMemo(() => {
    return result?.at(0)
  }, [result])

  const logout = useCallback(async () => {
    await user?.remove()
  }, [user])

  const value = useMemo(() => {
    return {
      user,
      logout,
      loading: isFetching,
    }
  }, [user, logout, isFetching])

  useEffect(() => {
    const subscription = observeAuthenticationState()?.subscribe(
      (requireAuthentication) => {
        if (requireAuthentication === 'IS_NOT_REQUIRED') {
          closeSnackbar('require-auth')
        }
        if (user && requireAuthentication === 'IS_REQUIRED') {
          enqueueSnackbar(formatMessage(messages.sessionExpired), {
            variant: 'warning',
            persist: true,
            key: 'require-auth',
            preventDuplicate: true,
            action: <LoginButton variant="outlined" color="inherit" />,
          })
        }
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [
    user,
    enqueueSnackbar,
    closeSnackbar,
    observeAuthenticationState,
    formatMessage,
  ])

  if (isFetching) {
    return <AppLoader />
  }

  return (
    <UserContext.Provider value={value}>
      <PostHogAnalytics />
      <Stack sx={contentSx}>
        <Header />
        <Stack sx={mainSx} component="main">
          {!user && <LoginPage />}
          {user && <Outlet />}
        </Stack>
        <Footer />
      </Stack>
      <OfflineErrorAlerts />
    </UserContext.Provider>
  )
}
